import { useState, useMemo } from 'react';
import { Button } from '@chakra-ui/react';

import { CardContainer, CostItem } from 'shared-components';

import { FamilySponsorship, FundingSummary, IFundingSourceText, FamilySponsorshipMap } from '../../../../types';
import SponsorModal from './modal';
import { hashToArray } from '../../../../utilities/helpers';
import { formatCurrency } from '../../../../utilities/formatting';
import { accountTypeLabel } from './utils';
import { getFundingContribution } from '../../../../utilities/getContributionAmount';

interface IProps {
  sponsorships: FamilySponsorshipMap;
  fundingSummary: FundingSummary;
  fundingSourceData: IFundingSourceText;
  config: any;
  hasPartner: boolean;
  onCreate: (data: FamilySponsorship) => void;
  onUpdate: (key: string, data: FamilySponsorship) => void;
  onRemove: (key: string) => void;
}

function BankSponsors(props: IProps) {
  const id = 'familySponsorship';

  const sponsorshipsList = useMemo(() => hashToArray(props.sponsorships), [props.sponsorships]);
  const [activeSponsorKey, setActiveSponsorKey] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const onEdit = (key: string) => {
    setActiveSponsorKey(key);
    setShowModal(true);
  };

  const onAddNew = () => {
    setActiveSponsorKey(null);
    setShowModal(true);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setActiveSponsorKey(null);
  };

  const renderItems = () => {
    return sponsorshipsList.map(({ key, item }) => {
      return (
        <CostItem
          key={key}
          id={`${id}-${key}`}
          label={accountTypeLabel(item.accountType)}
          value={`£${formatCurrency(item.sponsorshipAmount)}`}
          hasEdit={true}
          hasClose={true}
          onEditClick={() => onEdit(key)}
          onCloseClick={() => props.onRemove(key)}
        />
      );
    });
  };

  const buttonVariant = props.fundingSummary.totalRemaining <= 0 || sponsorshipsList.length > 0 ? 'secondary' : 'solid';

  const renderButton = () => {
    return (
      <Button id={`${id}-button`} onClick={onAddNew} variant={buttonVariant} size={'lg'}>
        {sponsorshipsList.length === 0 ? props.fundingSourceData.button : props.fundingSourceData.buttonAlt}
      </Button>
    );
  };

  const renderSponsorModal = () => {
    const sponsor = sponsorshipsList.find(({ key }) => key === activeSponsorKey);
    const contributionAmount =
      getFundingContribution(sponsor?.key ?? '', props.fundingSummary) || sponsor?.item.sponsorshipAmount || 0;
    return (
      <SponsorModal
        {...(sponsor ? sponsor.item : {})}
        key={sponsor ? sponsor.key : 'new'}
        sponsorKey={sponsor ? sponsor.key : null}
        id={`${id}-modal`}
        fundingSummary={props.fundingSummary}
        contributionAmount={contributionAmount}
        saveButtonText={props.fundingSourceData.saveButtonText}
        config={props.config}
        hasPartner={props.hasPartner}
        onSave={data => {
          if (sponsor) {
            props.onUpdate(sponsor.key, data);
          } else {
            props.onCreate(data);
          }
        }}
        onClose={onCloseModal}
      />
    );
  };

  return (
    <CardContainer title={props.fundingSourceData.label}>
      {renderItems()}
      {renderButton()}
      {showModal ? renderSponsorModal() : null}
    </CardContainer>
  );
}

export default BankSponsors;
